import { useState } from "react";
import { Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";

interface Card {
  id: number; serviceCategory: string; band: string; rateCents: number;
  effectiveFrom: string; effectiveTo: string | null;
  period: "current" | "scheduled" | "expired";
  capCents: number | null; aboveCap: boolean;
}
interface CatalogueRow {
  serviceCategory: string; band: string; supportItemNumber: string; unit: string;
  capCents: number; effectiveRateCents: number; priced: boolean;
}
interface RateCardsResponse { on: string; cards: Card[]; catalogue: CatalogueRow[] }

const bandLabel: Record<string, string> = {
  weekday_day: "Weekday day", weekday_evening: "Weekday evening", weekday_night: "Weekday night",
  saturday: "Saturday", sunday: "Sunday", public_holiday: "Public holiday", sleepover: "Sleepover",
};
const categoryLabel = (c: string) => c.replace(/_/g, " ").replace(/^./, (ch) => ch.toUpperCase());
const aud = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const periodChip: Record<Card["period"], string> = {
  current: "bg-teal-50 text-teal-800",
  scheduled: "bg-amber-50 text-amber-800",
  expired: "bg-slate-100 text-mut",
};

/**
 * Org rate cards. Bands with no rate of their own bill at the NDIS price
 * limit, so an org that never touches this screen is billed exactly as before
 * — which is why the catalogue table shows every band, priced or not.
 */
export default function RateCards() {
  const rates = useFetch<RateCardsResponse>("/v1/billing/rate-cards");
  const [form, setForm] = useState({ key: "", dollars: "", effectiveFrom: "" });
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Finance and owners price supports; everyone else gets a 403 here, and no
  // section at all rather than an empty one they cannot use.
  if (rates.error) return null;
  const data = rates.data;
  if (!data) return null;


  async function save(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setMsg(null);
    const [serviceCategory, band] = form.key.split("|");
    const rateCents = Math.round(Number(form.dollars) * 100);
    if (!serviceCategory || !band) { setErr("Pick a support band"); return; }
    if (!Number.isFinite(rateCents) || rateCents <= 0) { setErr("Enter a rate in dollars"); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveFrom)) { setErr("Pick the date this rate starts"); return; }
    try {
      const saved = await api<{ warning?: string }>("/v1/billing/rate-cards", {
        method: "POST",
        body: JSON.stringify({ serviceCategory, band, rateCents, effectiveFrom: form.effectiveFrom }),
      });
      setMsg(saved.warning ?? "Rate saved.");
      setForm({ key: "", dollars: "", effectiveFrom: "" });
      rates.reload();
    } catch (e2) { setErr((e2 as Error).message); }
  }

  async function remove(card: Card) {
    if (!confirm(`Remove the ${bandLabel[card.band] ?? card.band} rate from ${card.effectiveFrom}?`)) return;
    setErr(null); setMsg(null);
    try {
      await api(`/v1/billing/rate-cards/${card.id}`, { method: "DELETE" });
      setMsg("Rate removed.");
      rates.reload();
    } catch (e2) { setErr((e2 as Error).message); }
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-medium text-ink">Rate card</h2>
      <p className="text-sm text-mut">
        What you charge per support band. Anything you leave unpriced bills at the NDIS price
        limit, and a rate above the limit is billed at the limit.
      </p>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Your rate for each support band, as at {data.on}</caption>
          <thead>
            <tr>
              <th className="th py-2" scope="col">Support</th>
              <th className="th px-2 py-2" scope="col">Band</th>
              <th className="th px-2 py-2 text-right" scope="col">NDIS limit</th>
              <th className="th py-2 text-right" scope="col">Billing today</th>
            </tr>
          </thead>
          <tbody>
            {data.catalogue.map((row) => (
              <tr key={`${row.serviceCategory}|${row.band}`} className="border-t border-edge-soft">
                <td className="px-5 py-2 text-body">
                  {categoryLabel(row.serviceCategory)}
                  <span className="block font-mono text-xs text-faint">{row.supportItemNumber}</span>
                </td>
                <td className="px-2 py-2 text-body">{bandLabel[row.band] ?? row.band}</td>
                <td className="px-2 py-2 text-right text-mut">{aud(row.capCents)}{row.unit === "EA" ? " ea" : "/h"}</td>
                <td className="px-5 py-2 text-right font-medium text-ink">
                  {aud(row.effectiveRateCents)}
                  {!row.priced && <span className="ml-2 chip bg-slate-100 text-mut">at limit</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={save} className="card p-4 flex flex-wrap gap-3 items-end">
        <label className="block grow">
          <span className="label">Support band</span>
          <select value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} className="input">
            <option value="">Select a band…</option>
            {data.catalogue.map((row) => (
              <option key={`${row.serviceCategory}|${row.band}`} value={`${row.serviceCategory}|${row.band}`}>
                {categoryLabel(row.serviceCategory)} · {bandLabel[row.band] ?? row.band} (limit {aud(row.capCents)})
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label">Your rate (AUD)</span>
          <input type="number" step="0.01" min="0.01" value={form.dollars} placeholder="65.47"
            onChange={(e) => setForm({ ...form, dollars: e.target.value })} className="input" />
        </label>
        <label className="block">
          <span className="label">Applies from</span>
          <input type="date" value={form.effectiveFrom}
            onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} className="input" />
        </label>
        <button className="btn-dark">Save rate</button>
        {msg && <p role="status" className="text-sm text-teal-700 w-full">{msg}</p>}
        {err && <p role="alert" className="text-sm text-red-600 w-full">{err}</p>}
      </form>

      {data.cards.length > 0 && (
        <div className="card overflow-x-auto">
          <p className="px-5 py-3 text-sm font-medium text-ink border-b border-edge-soft">
            Rate periods
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="th py-2" scope="col">Band</th>
                <th className="th px-2 py-2" scope="col">From</th>
                <th className="th px-2 py-2" scope="col">To</th>
                <th className="th px-2 py-2 text-right" scope="col">Rate</th>
                <th className="th py-2 text-right" scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.cards.map((card) => (
                <tr key={card.id} className="border-t border-edge-soft">
                  <td className="px-5 py-2 text-body">
                    {categoryLabel(card.serviceCategory)} · {bandLabel[card.band] ?? card.band}
                  </td>
                  <td className="px-2 py-2 text-mut">{card.effectiveFrom}</td>
                  <td className="px-2 py-2 text-mut">{card.effectiveTo ?? "—"}</td>
                  <td className="px-2 py-2 text-right text-body">
                    {aud(card.rateCents)}
                    {card.aboveCap && <span className="ml-2 chip bg-amber-50 text-amber-800">above limit</span>}
                  </td>
                  <td className="px-5 py-2 text-right">
                    <span className={`chip ${periodChip[card.period]}`}>{card.period}</span>
                    <button onClick={() => remove(card)}
                      aria-label={`Remove the ${bandLabel[card.band] ?? card.band} rate from ${card.effectiveFrom}`}
                      className="btn-secondary ml-2 px-2 py-1">
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
